import {
  Box,
  Button,
  Flex,
  Header,
  Icon,
  IconButton,
  IconMenuButton,
  Link,
  Select,
  styled,
  Table,
  Text,
  Tooltip,
} from '@driftt/dds'
import { faBoxArchive } from '@driftt/dds.icons/light/faBoxArchive'
import { faPenToSquare } from '@driftt/dds.icons/light/faPenToSquare'
import { faSparkles } from '@driftt/dds.icons/solid/faSparkles'
import { DropdownMenuOptions } from '@driftt/dds/dist/packages/core/src/lib/components/DropdownMenu'
import { Subnav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import React, { useEffect } from 'react'
import { useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'

import {
  archiveScenario,
  deleteScenario,
  gerReducedScenarios,
  getActiveCustomScenariosCount,
  ReducedScenarioEntity,
  restoreScenario,
  Scenario,
  updateScenario,
} from 'api/scenarios'
import { APP_PAGE_TITLES } from 'constants/pageTitles'
import { MAX_ACTIVE_SCENARIOS } from 'constants/scenarios'
import useDocumentTitle from 'hooks/usePageTitle'
import { useScenariosList } from 'hooks/useScenariosList'
import BoltLogo from 'images/bolt-logo'
import { userHasAnyPlaybookAccess } from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'
import { capitalizeFirstLetter } from 'utils/index'

import ConfirmationModal from './ConfirmationModal'
import ModifyNameConfiramtionModal from './ModifyNameConfiramtionModal'
import ScenarioTemplatesModal from './ScenarioTemplatesModal'
import TrainingModal from './TrainingInfoModal'

type ScenariosStatus = 'ENABLED' | 'ARCHIVED' | 'DELETED'

interface ScenarioStatusOption {
  label: string
  value: ScenariosStatus
}

interface ConfirmationModalOptions {
  scenarioId: string
  action: 'archive' | 'restore' | 'delete'
}

interface UpdateNameModalOptions {
  scenarioId: string
  currentName: string
}

const SCENARIO_STATUS_OPTIONS: ScenarioStatusOption[] = [
  {
    label: 'Active scenarios',
    value: 'ENABLED',
  },
  {
    label: 'Archived scenarios',
    value: 'ARCHIVED',
  },
]

const TableCard = styled(Flex, {
  background: '$white',
  boxShadow: '$raised08',
  borderRadius: '$1',
  overflow: 'hidden',
})

const TextTruncated = styled(Text, {
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  width: 194,
  '-webkit-line-clamp': 3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const ScenariosTable = () => {
  const [scenariosStatus, setScenariosStatus] = useState<ScenarioStatusOption>(
    SCENARIO_STATUS_OPTIONS[0]
  )

  const [sidebarIsOpen, setSidebarIsOpen] = useState(true)
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false)
  const [isOpenModifyNameModal, setIsOpenModifyNameModal] = useState<
    UpdateNameModalOptions | undefined
  >(undefined)
  const [currentActiveScenariosCount, setCurrentActiveScenariosCount] =
    useState(0)
  const [confirmationModalOptions, setConfirmationModalOptions] = useState<
    ConfirmationModalOptions | undefined
  >(undefined)
  const [allActiveScenarios, setAllActiveScenarios] = useState<Scenario[]>([])

  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()

  const orgId: string = useSelector(({ user }: RootStateOrAny) => user.orgId)
  const {
    isLoading,
    scenarios: ScenariosTableData = [],
    handleTableStateChange,
    refetchTableState,
    pagination,
    rowTotal,
  } = useScenariosList({
    status: scenariosStatus.value,
    archived: scenariosStatus.value !== 'ENABLED',
    orgId,
  })

  useEffect(() => {
    refetchTableState()
    if (scenariosStatus.value === 'ENABLED') {
      setAllActiveScenarios(ScenariosTableData)
    }
  }, [refetchTableState, scenariosStatus, ScenariosTableData])

  useEffect(() => {
    async function fetchActiveScenariosCount() {
      try {
        const { data: count } = await getActiveCustomScenariosCount({ orgId })
        setCurrentActiveScenariosCount(count)
      } catch (e) {
        console.warn(e)
      }
    }

    fetchActiveScenariosCount()
  }, [orgId, scenariosStatus, ScenariosTableData])

  useDocumentTitle(APP_PAGE_TITLES.SCENARIO)

  const SUB_NAV_NODES = getSubNavNodes()

  const dispatch = useDispatch()

  const isAtScenariosLimit = currentActiveScenariosCount >= MAX_ACTIVE_SCENARIOS

  const handleOnStatusChange = (value: ScenarioStatusOption) => {
    setScenariosStatus(value)
  }
  const [enableFetchScenarios, setEnableFetchScenarios] =
    useState<boolean>(true)

  const { data: allPossibleScenarios } = useQuery<ReducedScenarioEntity[]>({
    queryKey: ['allPossibleScenarios'],
    queryFn: () =>
      gerReducedScenarios().then(({ data }) => {
        setEnableFetchScenarios(false)
        return data
      }),
    enabled: enableFetchScenarios,
  })

  const onArchive = useMutation(archiveScenario, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully archived scenario`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not archive scenario`,
        })
      )
    },
  })

  const onRestore = useMutation(restoreScenario, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully restored scenario`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not restore scenario`,
        })
      )
    },
  })

  const onDelete = useMutation(deleteScenario, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully deleted scenario`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not delete scenario`,
        })
      )
    },
  })

  const onUpdate = useMutation(updateScenario, {
    onSuccess: () => {
      refetchTableState()
      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Successfully updated scenario name`,
        })
      )
    },
    onError: () => {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not update scenario name`,
        })
      )
    },
  })

  const ACTIONS = {
    archive: onArchive.mutate,
    restore: onRestore.mutate,
    delete: onDelete.mutate,
  }

  const onEdit = (scenarioId: string) => {
    window &&
      window.open(`/playbooks/ai-scenarios/editor/${scenarioId}`, '_self')
  }

  const onCreateNew = (id?: string) => {
    window &&
      window.open(`/playbooks/ai-scenarios/editor/new-scenario/${id}`, '_self')
  }

  const confirmationModalAction = () => {
    if (!confirmationModalOptions) return
    const { scenarioId, action } = confirmationModalOptions
    ACTIONS[action]({ scenarioId, orgId })
    setConfirmationModalOptions(undefined)
  }
  const keepScenarioName = () => {
    if (!isOpenModifyNameModal) return
    const { scenarioId } = isOpenModifyNameModal
    ACTIONS['restore']({ scenarioId: scenarioId, orgId })
    setIsOpenModifyNameModal(undefined)
  }
  const onUpdateScenario = (newScenarioName?: string) => {
    if (!isOpenModifyNameModal) return
    const { scenarioId } = isOpenModifyNameModal
    const scenario = ScenariosTableData.find(
      (scenario) => scenario.scenarioId === scenarioId
    )
    onUpdate.mutate({ orgId, payload: { ...scenario, name: newScenarioName } })
    ACTIONS['restore']({ scenarioId, orgId })
    setIsOpenModifyNameModal(undefined)
  }

  const confirmationModalOptionAction = (
    action: string | undefined,
    scenarioId?: string
  ) => {
    const scenario = ScenariosTableData.find(
      (scenario) => scenario.scenarioId === scenarioId
    )
    if (action === 'restore' && scenario?.name.includes('Archived')) {
      setEnableFetchScenarios(true)
      setIsOpenModifyNameModal({
        scenarioId: scenario.scenarioId,
        currentName: scenario.name,
      })
    } else {
      confirmationModalAction()
    }
  }

  const ActionsCell = ({ rowData }: { rowData: Scenario }) => {
    const { scenarioId, driftManaged } = rowData

    const MENU_ACTIONS_BY_STATUS = Object.freeze({
      ENABLED: [
        {
          title: 'Edit',
          action: () => onEdit(scenarioId),
          icon: faPenToSquare,
        },
        {
          title: 'Archive',
          action: () =>
            setConfirmationModalOptions({ scenarioId, action: 'archive' }),
          icon: faBoxArchive,
        },
      ],
      ARCHIVED: [
        {
          name: 'View',
          action: () => onEdit(scenarioId),
        },
        {
          name: 'Restore',
          action: () =>
            setConfirmationModalOptions({ scenarioId, action: 'restore' }),
          disabled: isAtScenariosLimit && !driftManaged,
        },
        {
          name: 'Delete',
          action: () =>
            setConfirmationModalOptions({ scenarioId, action: 'delete' }),
          disabled: driftManaged,
        },
      ],
      DELETED: [],
    })

    return (
      <Flex>
        {scenariosStatus.value === 'ENABLED' &&
          MENU_ACTIONS_BY_STATUS[scenariosStatus.value].map((action) => (
            <IconButton
              icon={action.icon}
              title={action.title}
              onClick={action.action}
            />
          ))}
        {scenariosStatus.value === 'ARCHIVED' && (
          <IconMenuButton
            iconVariant="spillover"
            title=""
            options={
              MENU_ACTIONS_BY_STATUS[
                scenariosStatus.value
              ] as unknown as DropdownMenuOptions
            }
          />
        )}
      </Flex>
    )
  }

  return (
    <>
      {hasAnyPlaybookAccess && (
        <Subnav
          title="Chatbot"
          nodes={SUB_NAV_NODES}
          open={sidebarIsOpen}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <Flex direction="column" gap={0} fullWidth fullHeight>
        <TrainingModal />
        <Box
          fullWidth
          css={{ padding: '$3 $6', borderBottom: '$gray3 1px solid' }}
        >
          <Flex fullWidth justifyContent="space-between">
            <Header>
              <Icon
                size="large"
                icon={faSparkles}
                color="purple7"
                css={{ verticalAlign: 'baseline', marginRight: '$1' }}
              />
              Scenarios
            </Header>
            <Flex direction="row" gap={3} alignItems="center">
              <Link href="/playbooks/testing" size="medium" variant="inline">
                Test AI responses
              </Link>
              <Tooltip
                content={
                  isAtScenariosLimit &&
                  `You’ve reached your max of 5 scenarios.`
                }
                side="left"
              >
                <Button
                  onClick={() => setIsTemplatesModalOpen(true)}
                  disabled={
                    isAtScenariosLimit ||
                    (isLoading && !allActiveScenarios.length)
                  }
                >
                  Create scenario
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        </Box>
        <Box
          fullWidth
          height="100%"
          css={{ background: '$gray2', padding: '$3 $6', overflow: 'auto' }}
        >
          <TableCard direction="column" gap={0}>
            <Flex
              fullWidth
              direction="row"
              justifyContent="flex-end"
              css={{ padding: '12px' }}
            >
              <Select
                options={SCENARIO_STATUS_OPTIONS}
                label=""
                value={scenariosStatus}
                onChange={(newValue): void =>
                  handleOnStatusChange(newValue as ScenarioStatusOption)
                }
              />
            </Flex>
            <Table
              isLoading={isLoading}
              rows={ScenariosTableData}
              disableBoxShadow={true}
              columns={[
                {
                  value: 'name',
                  header: 'Scenario',
                  size: 176,
                  cell: (row) => (
                    <Link
                      href={`/playbooks/ai-scenarios/editor/${row.scenarioId}/`}
                      variant="object"
                      css={{ display: 'block', height: '100%' }}
                    >
                      <Flex direction="row" alignItems="center" gap={0}>
                        {row.driftManaged && (
                          <BoltLogo height="14" width="17" />
                        )}
                        {row.name}
                      </Flex>
                    </Link>
                  ),
                },
                {
                  value: 'prompt',
                  header: 'Description',
                  size: 194,
                  cell: (row) => (
                    <TextTruncated>{row.prompt ?? '--'}</TextTruncated>
                  ),
                },
                {
                  value: 'instruction',
                  header: 'Reply guidelines',
                  size: 194,
                  cell: (row) => (
                    <TextTruncated>
                      {row.useInstruction && row.instruction
                        ? row.instruction
                        : '--'}
                    </TextTruncated>
                  ),
                },
                {
                  value: 'exit',
                  header: 'Offramp',
                  size: 194,
                  cell: (row) => (
                    <div>
                      <Text css={{ display: 'block', height: '100%' }}>
                        {(row.useExit &&
                          row.exit &&
                          capitalizeFirstLetter(row.exit.toLowerCase())) ||
                          '--'}
                      </Text>
                    </div>
                  ),
                },
                {
                  value: 'scenarioId',
                  header: '',
                  size: 20,
                  cell: (row) => (
                    <Box css={{ display: 'block', height: '100%' }}>
                      <ActionsCell rowData={row} />
                    </Box>
                  ),
                },
              ]}
              pagination={
                scenariosStatus.value !== 'ENABLED'
                  ? {
                      defaultPagination: pagination,
                      rowTotal,
                    }
                  : undefined
              }
              emptyState={{
                minHeight: 600,
                title: '',
              }}
              onStateChange={handleTableStateChange}
            />
          </TableCard>
        </Box>
      </Flex>

      <ModifyNameConfiramtionModal
        isOpen={!!isOpenModifyNameModal}
        onClose={() => keepScenarioName()}
        onAction={(newScenarioName) => {
          onUpdateScenario(newScenarioName)
        }}
        scenarioCurrentName={isOpenModifyNameModal?.currentName}
        possibleScenarios={allPossibleScenarios}
      />
      <ConfirmationModal
        isOpen={!!confirmationModalOptions}
        action={confirmationModalOptions?.action}
        onClose={() => setConfirmationModalOptions(undefined)}
        onAction={() =>
          confirmationModalOptionAction(
            confirmationModalOptions?.action,
            confirmationModalOptions?.scenarioId
          )
        }
      />
      <ScenarioTemplatesModal
        isOpen={isTemplatesModalOpen}
        onAction={(templateId) => onCreateNew(templateId)}
        onClose={() => setIsTemplatesModalOpen(false)}
        templatesUsed={allActiveScenarios.map(
          (scenario) => scenario.templateId
        )}
      />
    </>
  )
}

export default ScenariosTable
